// One run on a phone: /orgs/:orgId/ws/:workspaceId/runs/:jobId. Big status,
// a progress bar, one row per block with its light, and the Logs tail —
// no canvas (React Flow is a desktop tool), everything the team needs to
// watch a scheduled job finish from the train.
import React, { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useJob } from '../data/firestore';
import { useEntityDoc } from '../data/entities';
import RunLogsPanel from '../components/nqube/RunLogsPanel';

const ACTIVE = new Set(['queued', 'planning', 'running', 'stitching']);
const LIGHT = { queued: 'queued', running: 'running', done: 'done', skipped: 'skipped', error: 'error' };
const dur = (ms) => (ms < 60e3 ? `${Math.round(ms / 1000)} s` : `${Math.floor(ms / 60e3)} min ${Math.round((ms % 60e3) / 1000)} s`);

export default function PipelineRun() {
  const { orgId, workspaceId, jobId } = useParams();
  const { job } = useJob(jobId);
  const pipelineId = job?.spec?.pipelineId || null;
  const { doc: pipeline } = useEntityDoc('pipeline', { org: orgId, workspace: job?.spec?.workspaceId || workspaceId }, pipelineId || '_');
  const [logBlock, setLogBlock] = useState(null);

  if (job === undefined) return <div className="cs-page nq-run-page"><p className="cs-help">Loading…</p></div>;
  if (job === null) return <div className="cs-page nq-run-page"><p className="cs-help">Run not found.</p></div>;
  const active = ACTIVE.has(job.status);
  const blocks = pipeline?.graph?.blocks || [];
  const live = job.liveBlocks || {};
  const results = Object.fromEntries((job.output?.results || []).map((r) => [r.blockId, r]));
  const rows = blocks.length ? blocks : Object.keys(live).map((id) => ({ id, label: id, type: '' }));
  const doneCount = rows.filter((b) => ['done', 'skipped'].includes(live[b.id]?.status)).length;
  const pct = typeof job.progress === 'number' && job.progress > 0 ? Math.round(job.progress * 100) : rows.length ? Math.round((doneCount / rows.length) * 100) : 0;
  const elapsed = (job.finishedAt || Date.now()) - (job.startedAt || job.createdAt || Date.now());

  return (
    <div className="cs-page nq-run-page" data-nova-note={`Run ${jobId} of ${pipeline?.name || pipelineId || 'a pipeline'}: ${job.status}, ${pct}% (${doneCount}/${rows.length} blocks)${job.spec?.trigger === 'schedule' ? ', fired by its schedule' : ''}.`}>
      <p style={{ margin: 0 }}>
        {pipelineId ? <Link to={`/orgs/${orgId}/ws/${job.spec?.workspaceId || workspaceId}/pipelines/${pipelineId}`} className="cs-help" style={{ textDecoration: 'none' }}>← {pipeline?.name || 'Workflow'}</Link> : <Link to={`/orgs/${orgId}/workflows`} className="cs-help" style={{ textDecoration: 'none' }}>← Workflows</Link>}
      </p>
      <div className="nq-run-hero">
        <h1 style={{ margin: 0, fontSize: 22 }}>{job.title || pipeline?.name || jobId}</h1>
        <span className={`nq-run-badge${job.status === 'error' ? ' is-error' : ''}`}>
          {active && <span className="nq-light nq-light-running" />} {job.status}{job.spec?.trigger === 'schedule' ? ' · scheduled' : job.spec?.testId ? ' · test' : ''}
        </span>
      </div>
      <div className="nq-run-bar" aria-label={`${pct}% complete`}><i style={{ width: `${pct}%` }} /></div>
      <p className="cs-help" style={{ fontSize: 13, margin: '6px 0 0' }}>
        {doneCount}/{rows.length} blocks · {dur(elapsed)}{job.revisionId ? ` · design ${job.revisionId}` : ''}{job.error ? ` · ${job.error}` : ''}
      </p>

      <ul className="nq-run-blocks">
        {rows.map((b) => {
          const s = live[b.id] || {};
          const r = results[b.id];
          const status = s.gateClosed ? 'skipped' : s.status || (r ? (r.skipped ? 'skipped' : r.ok ? 'done' : 'error') : 'queued');
          return (
            <li key={b.id}>
              <span className={`nq-light nq-light-${LIGHT[status] || 'queued'}`} />
              <span style={{ flex: 1 }}>
                {b.label || b.type || b.id}
                <span className="cs-help" style={{ display: 'block' }}>
                  {s.gateClosed ? 'branch closed' : status}{typeof s.ms === 'number' ? ` · ${(s.ms / 1000).toFixed(1)} s` : ''}{s.retry ? ` · retry ${s.retry}` : ''}{(s.error || r?.error) ? ` — ${s.error || r.error}` : ''}
                </span>
              </span>
              <button type="button" className="cs-btn cs-btn-outline cs-btn-sm" onClick={() => setLogBlock(logBlock === b.id ? null : b.id)} aria-pressed={logBlock === b.id}>log</button>
            </li>
          );
        })}
      </ul>

      <div className="cs-card" style={{ padding: 12, marginTop: 12 }}>
        <strong style={{ color: 'var(--text)', fontSize: 13 }}>Logs</strong>
        <div style={{ marginTop: 6 }}>
          <RunLogsPanel
            jobId={jobId}
            orgId={orgId} workspaceId={job.spec?.workspaceId || workspaceId} pipelineId={pipelineId || 'run'}
            blocks={rows} selectedBlock={logBlock} onSelectBlock={setLogBlock} active={active}
          />
        </div>
      </div>
      {job.output?.outputs && !active && (
        <details className="cs-card" style={{ padding: 12, marginTop: 12 }}>
          <summary className="cs-help" style={{ fontSize: 13, cursor: 'pointer' }}>Outputs</summary>
          <pre style={{ fontSize: 12, overflow: 'auto', maxHeight: 320, whiteSpace: 'pre-wrap' }}>{JSON.stringify(job.output.outputs, null, 2)}</pre>
        </details>
      )}
    </div>
  );
}
